/**
 * Coloured backgrounds for text posts — the single source of truth for both the
 * composer's picker and the feed card that renders the result.
 *
 * ── The stored value is DATA, not CSS ───────────────────────────────────────
 * `membership_posts.post_bg` holds a short tagged string:
 *
 *      p:sunset                 a preset below, by id
 *      s:ff0055                 custom solid
 *      g:135:ff0055,3b3bea      custom gradient — angle, then 2 or 3 stops
 *
 * It ends up inside a `style` attribute, so it can never be a CSS string the
 * client made up: the API validates this exact grammar and stores nothing else,
 * and `parseBg` below is the only thing that turns it back into CSS. Keeping the
 * presets' colours HERE and only their ids on the server also means restyling
 * `sunset` is a one-line change that applies to every post already using it.
 */

/** Preset ids MUST stay in sync with POST_BG_PRESETS in MembershipFeedController. */
export const POST_BG_PRESETS = {
  // ── Solids ────────────────────────────────────────────────────────────────
  // Deep and slightly desaturated rather than pure hues. A flat #ff0000 behind
  // white text looks like a warning banner; these read as stationery.
  ink:     { css: "#111827", fg: "#ffffff" },
  wine:    { css: "#86123f", fg: "#ffffff" },
  cobalt:  { css: "#1442c9", fg: "#ffffff" },
  pine:    { css: "#0b6b52", fg: "#ffffff" },
  violet:  { css: "#5b21b6", fg: "#ffffff" },
  clay:    { css: "#a03a1e", fg: "#ffffff" },

  // ── Gradients ─────────────────────────────────────────────────────────────
  // Mostly analogous — neighbouring hues, so the blend stays clean instead of
  // passing through the muddy middle two opposite hues always produce.
  nova:    { css: "linear-gradient(135deg,#f9295f 0%,#a02bd6 55%,#3b3bea 100%)", fg: "#ffffff" },
  aurora:  { css: "linear-gradient(135deg,#00b9a3 0%,#0b63c5 58%,#5b21b6 100%)", fg: "#ffffff" },
  dusk:    { css: "linear-gradient(140deg,#2b5876 0%,#4e4376 100%)", fg: "#ffffff" },
  blaze:   { css: "linear-gradient(135deg,#ff512f 0%,#dd2476 100%)", fg: "#ffffff" },
  orchid:  { css: "linear-gradient(135deg,#8e2de2 0%,#4a00e0 100%)", fg: "#ffffff" },
  lagoon:  { css: "linear-gradient(135deg,#0093e9 0%,#80d0c7 100%)", fg: "#06373a" },

  // ── Light ─────────────────────────────────────────────────────────────────
  // The reason `fg` is per-preset rather than always white.
  gilt:    { css: "linear-gradient(135deg,#f7971e 0%,#ffd200 100%)", fg: "#3a2a00" },
  blush:   { css: "linear-gradient(135deg,#ee9ca7 0%,#ffdde1 100%)", fg: "#6b2737" },
};

/** Order shown in the picker: solids, then gradients, then the light pair. */
export const POST_BG_ORDER = [
  "ink", "wine", "cobalt", "pine", "violet", "clay",
  "nova", "aurora", "dusk", "blaze", "orchid", "lagoon",
  "gilt", "blush",
];

/** A background post is a SHORT post. Past this the picker drops the colour. */
export const POST_BG_MAX_CHARS = 250;

const HEX6 = /^[0-9a-f]{6}$/;

/** WCAG relative luminance of a 6-digit hex, 0 (black) → 1 (white). */
function luminance(hex) {
  const ch = (i) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
}

/**
 * Readable text colour for a custom background. Computed, never stored — a user
 * free to pick pale yellow is otherwise one tap away from invisible white text,
 * and no amount of picker UI reliably stops that.
 *
 * The threshold sits at 0.45 rather than 0.5 because white-on-mid-tone stays
 * legible slightly further down the scale than black-on-mid-tone does.
 */
function readableFg(stops) {
  const avg = stops.reduce((sum, h) => sum + luminance(h), 0) / stops.length;
  return avg > 0.45 ? "#111827" : "#ffffff";
}

/**
 * Readable ink for a single solid colour — the same rule as `readableFg`, for
 * callers holding one colour rather than a set of gradient stops (the loader
 * puts a page icon on a brand-filled disc, and a pale brand would swallow a
 * white glyph exactly the way it swallows white text).
 *
 * Anything not parseable as hex — a CSS name, `rgb()`, an empty string — comes
 * back white, which is what the app assumed before this existed.
 */
export function readableOn(color) {
  let hex = String(color || "").trim().toLowerCase().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/.test(hex)) hex = hex.replace(/./g, (c) => c + c);
  return HEX6.test(hex) ? readableFg([hex]) : "#ffffff";
}

/**
 * Stored string → { css, fg }, or null for "no background".
 *
 * Returns null rather than throwing on anything unrecognised: a row written by a
 * newer build (a preset this bundle does not have yet) must render as an ordinary
 * text post, not blow up the feed.
 */
export function parseBg(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (!v) return null;

  if (v.startsWith("p:")) return POST_BG_PRESETS[v.slice(2)] || null;

  if (v.startsWith("s:")) {
    const hex = v.slice(2);
    if (!HEX6.test(hex)) return null;
    return { css: `#${hex}`, fg: readableFg([hex]) };
  }

  if (v.startsWith("g:")) {
    const [, angleRaw, stopsRaw] = v.split(":");
    const angle = Number(angleRaw);
    if (!Number.isInteger(angle) || angle < 0 || angle > 360) return null;
    const stops = String(stopsRaw || "").split(",");
    if (stops.length < 2 || stops.length > 3 || !stops.every((h) => HEX6.test(h))) return null;
    const pct = stops.map((h, i) => `#${h} ${Math.round((i / (stops.length - 1)) * 100)}%`);
    return { css: `linear-gradient(${angle}deg,${pct.join(",")})`, fg: readableFg(stops) };
  }

  return null;
}

/** Picker state → the stored string. Returns "" for "no background". */
export function encodeBg(state) {
  if (!state) return "";
  if (state.preset) return POST_BG_PRESETS[state.preset] ? `p:${state.preset}` : "";
  const clean = (h) => String(h || "").replace(/^#/, "").toLowerCase();
  if (state.mode === "solid") {
    const hex = clean(state.solid);
    return HEX6.test(hex) ? `s:${hex}` : "";
  }
  if (state.mode === "gradient") {
    const stops = (state.stops || []).map(clean).filter((h) => HEX6.test(h));
    if (stops.length < 2) return "";
    const angle = Math.max(0, Math.min(360, Math.round(Number(state.angle) || 0)));
    return `g:${angle}:${stops.slice(0, 3).join(",")}`;
  }
  return "";
}

/**
 * Type size for a background post, by text length.
 *
 * A fixed size either wastes the canvas on three words or overflows it on three
 * lines, so it steps down instead — the same thing Facebook does, and the reason
 * the character cap exists at all: past ~250 there is no size that still reads
 * as a statement rather than a paragraph on a coloured box.
 */
export function bgFontSize(len) {
  if (len <= 40) return 30;
  if (len <= 90) return 25;
  if (len <= 160) return 21;
  return 18;
}
